import {TextArea, UploadBox} from "components/core";
import { useFormik } from "formik";
import { FC } from "react";
import {wrapClick, classNames} from "utils";
import {
  IResolveFATServiceOrderFormSchema,
  ExtraInfoSchema,
} from "./schema";
import lodash from "lodash";
import {XMarkIcon, ExclamationCircleIcon} from "@heroicons/react/24/outline";

export interface ExtraInfoFormProps {
  handleNext: (values: IResolveFATServiceOrderFormSchema["extraInfo"]) => void;
  handlePrevious: () => void;
  initialValues: IResolveFATServiceOrderFormSchema["extraInfo"];
  values: IResolveFATServiceOrderFormSchema;
  handleCancel: () => void;
  meterContractor: any;
}

const TestForm: FC<ExtraInfoFormProps> = ({
  initialValues,
  handleNext,
  handleCancel,
  meterContractor,
  handlePrevious,
}) => {

  const form = useFormik({
    initialValues,
    validationSchema: ExtraInfoSchema,
    onSubmit: (values) => {
      handleNext(values);
    },
    onReset: () => {
      handleCancel?.();
    },
  });

  const removeItem = (field: string, id: number) => {

    form.setFieldValue(field, [
      ...lodash
        .get(form.values, field)
        ?.filter((val: string, _id: number) => _id !== id),
    ]);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="space-y-6 divide-y divide-gray-200 p-6 flex-1 overflow-y-auto">
        <div>
          <span className="text-xs font-light">Extra Information</span>
          <div className="grid grid-cols-3 gap-6 mt-2">
            <div className="col-span-3">
              <span className="block text-sm font-medium text-gray-700">
                Observations
              </span>
              <div className="mt-1 w-full ">
                <table className="min-w-full divide-y divide-gray-300 border border-gray-300 rounded-md">
                  <thead className="bg-gray-50">
                    <tr className="divide-x divide-gray-200">
                      <th
                        scope="col"
                        className="w-[50px] whitespace-nowrap px-2 py-2 text-left text-sm font-semibold text-gray-900"
                      >
                        No.
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap w-full px-2 py-2 text-sm font-semibold text-gray-900 w-[38px]"
                      >
                        Observation
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-2 text-sm font-semibold text-gray-900 w-[38px]"
                      >
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                  {lodash.times(
                    form.values.observations?.length + 1,
                    (idx) => {
                      const id = `observations.${idx}`;
                      const isLast =
                        (form.values.observations?.length || 0) === idx;

                      return (
                        <tr key={idx} className="divide-x divide-gray-200">
                          <td className="p-0">
                            <input
                              key={idx}
                              type={"text"}
                              id={`index`}
                              name={`index`}
                              value={`${idx + 1}.`}
                              placeholder={"eg. 1"}
                              disabled
                              className={classNames(
                                "focus:ring-primary-500 focus:border-primary-500 border-gray-200 bg-gray-50",
                                "block w-[50px] sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                              )}
                            />
                          </td>
                          <td className="p-0 relative">
                            <input
                              key={idx}
                              type={"text"}
                              id={`${id}`}
                              name={`${id}`}
                              value={lodash.get(
                                form.values,
                                `${id}`,
                                ""
                              )}
                              onChange={form.handleChange}
                              onBlur={form.handleBlur}
                              placeholder={"eg. We noticed that..."}
                              className={classNames(
                                lodash.get(form.errors, `${id}`) &&
                                lodash.get(form.touched, `${id}`)
                                  ? "focus:ring-primary-500 focus:border-primary-500 ring-red-500 border-red-600 border"
                                  : "focus:ring-primary-500 focus:border-primary-500 border-gray-200 border-0 focus:border",
                                "outline-none block w-full sm:text-sm placeholder:font-light placeholder:text-gray-700 placeholder:text-xs h-[38px]"
                              )}
                            />
                          </td>
                          <td className="p-0 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-right w-[38px] h-[38px] items-center justify-center flex">
                            {!isLast && (
                              <button
                                type="button"
                                onClick={wrapClick(() =>
                                  removeItem("observations", idx)
                                )}
                                className="inline-flex items-center rounded-full border border-transparent p-1 text-red-500 hover:bg-red-300 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                              >
                                <XMarkIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    }
                  )}
                  </tbody>
                </table>
              </div>
              {form.errors?.observations && (
                <div className="rounded-md bg-red-50 p-4 mt-2">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationCircleIcon
                        className="h-5 w-5 text-red-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        {lodash.isString(form?.errors?.observations)
                          ? form?.errors?.observations
                          : lodash
                            .chain(form?.errors?.observations)
                            .map((observation: any) =>
                              observation
                                ? observation
                                : ""
                            )
                            .value()}
                      </h3>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="col-span-3">
              <TextArea
                id="conclusion"
                label="Conclusion"
                required={true}
                rows={10}
                placeholder="e.g. We therefore conclude..."
                {...form}
              />
            </div>
            <div className="col-span-3">
              <TextArea
                id="recommendations"
                label="Recommendations"
                rows={10}
                placeholder="e.g. We recommend..."
                {...form}
              />
            </div>
            <div className="col-span-3">
              <UploadBox
                label="Supporting documents"
                multiple
                maxFiles={10}
                id={"supportingImageUrls"}
                {...form}
              />
            </div>
            <div className="col-span-3">
              <UploadBox
                label="Routine Test Reports"
                multiple
                required={true}
                maxFiles={10}
                id={"routineTestReports"}
                {...form}
              />
            </div>

          </div>
        </div>
      </div>
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 sm:py-4 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
        <button
          type="button"
          data-invalid={!form.isValid}
          onClick={wrapClick(form.handleSubmit)}
          className="w-full inline-flex justify-center data-invalid:bg-gray-200 data-invalid:border-gray-400 data-invalid:cursor-not-allowed data-invalid:text-gray-600 rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
        >
          Next
        </button>
        <button
          type="button"
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={wrapClick(form.resetForm)}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={wrapClick(handlePrevious)}
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
        >
          Previous
        </button>
      </div>
    </div>
  );
};

export default TestForm;
